import { useEffect, useRef, useState } from 'preact/hooks';
import { Button } from '@components/buttons';
import { showToast } from '@components/toaster';
import { useIntl } from 'shared/intl/use-intl';
import { captureException } from 'client/lib/sentry';
import {
  AUDIO_TYPES,
  canRecord,
  captureErrorKind,
  CaptureErrorKind,
  formatDuration,
  Recording,
  recordingName,
  startRecording,
  stopStream,
} from 'client/lib/media-recording';

type Props = {
  onDone(file: File): void;
  onCancel(): void;
  onRecordingChange?(isRecording: boolean): void;
};

type RecorderError = CaptureErrorKind | 'empty' | 'interrupted';

const BAR_COUNT = 32;

function useErrorMessage() {
  const intl = useIntl();
  return (kind: RecorderError) => {
    if (kind === 'empty') {
      return intl('Nothing was recorded. Try again.');
    }
    if (kind === 'interrupted') {
      return intl('Recording stopped before it was saved. Try again.');
    }
    if (kind === 'blocked') {
      return intl(
        'Your microphone is blocked for this site. Allow it from the icon in the address bar, then try again.',
      );
    }
    if (kind === 'missing') {
      return intl('No microphone found. Connect one, then try again.');
    }
    if (kind === 'busy') {
      return intl('Another app is using your microphone. Close it, then try again.');
    }
    if (kind === 'unsupported') {
      return intl(
        "This browser can't record audio. Record on your device, then add the file with Add photo, video or file.",
      );
    }
    return intl("Recording didn't start. Try again.");
  };
}

function createAudioContext() {
  const AudioCtx: typeof AudioContext | undefined =
    window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtx) {
    return;
  }
  try {
    const ctx = new AudioCtx();
    void ctx.resume().catch(() => {});
    return ctx;
  } catch (err) {
    captureException(err as Error);
    return;
  }
}

function watchLevels(ctx: AudioContext, stream: MediaStream, bars: HTMLElement[]) {
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  ctx.createMediaStreamSource(stream).connect(analyser);
  const samples = new Uint8Array(analyser.fftSize);
  const history: number[] = new Array(bars.length).fill(0);
  let frame = 0;
  let lastPush = 0;

  function tick(now: number) {
    frame = requestAnimationFrame(tick);
    if (now - lastPush < 80) {
      return;
    }
    lastPush = now;
    analyser.getByteTimeDomainData(samples);
    let sum = 0;
    samples.forEach((value) => {
      const centered = (value - 128) / 128;
      sum += centered * centered;
    });
    const level = Math.min(1, Math.sqrt(sum / samples.length) * 4);
    history.shift();
    history.push(level);
    bars.forEach((bar, index) => {
      bar.style.height = `${Math.max(12, Math.round(history[index] * 100))}%`;
    });
  }
  frame = requestAnimationFrame(tick);

  return () => cancelAnimationFrame(frame);
}

export function VoiceRecorder({ onDone, onCancel, onRecordingChange }: Props) {
  const intl = useIntl();
  const errorMessage = useErrorMessage();
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<'starting' | 'recording' | 'stopping'>('starting');
  const [error, setError] = useState<RecorderError | undefined>(undefined);
  const [seconds, setSeconds] = useState(0);
  const recording = useRef<Recording | undefined>(undefined);
  const stream = useRef<MediaStream | undefined>(undefined);
  const bars = useRef<HTMLElement[]>([]);
  const isMounted = useRef(true);
  const wasInterrupted = useRef(false);
  const stopButton = useRef<HTMLButtonElement>(null);
  const retryButton = useRef<HTMLButtonElement>(null);
  const release = useRef(() => {});
  const stopRef = useRef(() => {});

  useEffect(() => {
    let disposed = false;
    let stopLevels = () => {};
    let timer: ReturnType<typeof setInterval> | undefined;
    const ctx = canRecord() ? createAudioContext() : undefined;
    release.current = () => {
      clearInterval(timer);
      stopLevels();
      stopLevels = () => {};
      stopStream(stream.current);
      stream.current = undefined;
    };
    wasInterrupted.current = false;
    setError(undefined);
    setStatus('starting');
    setSeconds(0);

    async function begin() {
      if (!canRecord()) {
        setError('unsupported');
        return;
      }
      let media: MediaStream;
      try {
        media = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        const kind = captureErrorKind(err);
        if (kind === 'failed') {
          captureException(err as Error);
        }
        setError(kind);
        return;
      }
      if (disposed) {
        stopStream(media);
        return;
      }
      stream.current = media;
      try {
        recording.current = startRecording(media, AUDIO_TYPES, () => {
          wasInterrupted.current = true;
          stopRef.current();
        });
      } catch (err) {
        captureException(err as Error);
        release.current();
        setError(captureErrorKind(err) === 'unsupported' ? 'unsupported' : 'failed');
        return;
      }
      if (ctx) {
        try {
          stopLevels = watchLevels(ctx, media, bars.current);
        } catch (err) {
          captureException(err as Error);
        }
      }
      const startedAt = Date.now();
      timer = setInterval(() => setSeconds((Date.now() - startedAt) / 1000), 250);
      setStatus('recording');
    }
    void begin();

    return () => {
      disposed = true;
      recording.current?.cancel();
      recording.current = undefined;
      release.current();
      void ctx?.close().catch(() => {});
    };
  }, [attempt]);

  useEffect(() => {
    onRecordingChange?.((status === 'recording' || status === 'stopping') && !error);
  }, [status, error]);

  useEffect(
    () => () => {
      isMounted.current = false;
      onRecordingChange?.(false);
    },
    [],
  );

  useEffect(() => {
    if (status === 'recording') {
      stopButton.current?.focus();
    }
  }, [status]);

  useEffect(() => {
    if (error) {
      retryButton.current?.focus();
    }
  }, [error]);

  const stop = async () => {
    const current = recording.current;
    if (!current || status !== 'recording') {
      return;
    }
    setStatus('stopping');
    recording.current = undefined;
    let blob: Blob | undefined;
    try {
      blob = await current.stop();
    } catch (err) {
      captureException(err as Error);
    } finally {
      release.current();
    }
    if (!isMounted.current) {
      return;
    }
    if (!blob) {
      setError('interrupted');
      return;
    }
    if (!blob.size) {
      setError('empty');
      return;
    }
    const type = blob.type || 'audio/webm';
    onDone(new File([blob], recordingName(intl('Voice note'), type), { type }));
    if (wasInterrupted.current) {
      showToast({
        type: 'warn',
        title: intl('Recording stopped'),
        message: intl('Your microphone disconnected. The note keeps what was recorded.'),
      });
    }
  };
  stopRef.current = stop;

  if (error) {
    return (
      <div
        class="flex flex-wrap items-center gap-3 px-4 py-3 border-t dark:border-gray-600 bg-red-50 dark:bg-red-950/40 rounded-b-lg"
        role="alert"
      >
        <p class="grow basis-56 text-sm text-red-700 dark:text-red-300">{errorMessage(error)}</p>
        <Button
          class="px-4 py-1.5 rounded-full border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-sm"
          onClick={onCancel}
        >
          {intl('Cancel')}
        </Button>
        {error !== 'unsupported' && (
          <button
            ref={retryButton}
            type="button"
            class="px-4 py-1.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
            onClick={() => setAttempt((n) => n + 1)}
          >
            {intl('Try again')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      class="flex items-center gap-3 px-4 py-2 border-t dark:border-gray-600"
      role="group"
      aria-label={intl('Recording voice note')}
    >
      <span
        class={`w-2.5 h-2.5 shrink-0 rounded-full bg-red-600 ${status === 'recording' ? 'animate-pulse' : 'opacity-40'}`}
      />
      <span class="min-w-10 text-sm tabular-nums text-gray-700 dark:text-gray-200">
        {formatDuration(seconds)}
      </span>
      <span class="flex grow min-w-10 h-7 items-center gap-0.5 overflow-hidden" aria-hidden="true">
        {Array.from({ length: BAR_COUNT }, (_, index) => (
          <i
            key={index}
            ref={(el) => {
              if (el) {
                bars.current[index] = el;
              }
            }}
            class="block w-[3px] shrink-0 rounded-sm bg-indigo-500 transition-[height] duration-75"
            style={{ height: '12%' }}
          />
        ))}
      </span>
      <Button
        class="px-4 py-1.5 rounded-full border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-sm"
        onClick={onCancel}
      >
        {intl('Cancel')}
      </Button>
      <button
        ref={stopButton}
        type="button"
        class="px-4 py-1.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60"
        disabled={status !== 'recording'}
        onClick={stop}
      >
        {intl('Stop')}
      </button>
    </div>
  );
}
